// useAppSearchInterface.js - FIXED prebuilt sets clearing on new search

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { calculatePrebuiltAppSets } from "../components/PrebuiltAppSetsCalculator";
import appApiService from "../services/AppApiService";
import apiService from "../services/apiService";
import { getRecommendedThreshold, relevanceFromDistance } from "../utils";

// Fallback cosine distance for terms rebuilt from an API payload
// (threshold sheet: Apps -> Strong Match).
const DEFAULT_TERM_THRESHOLD = getRecommendedThreshold("APPS");

// Attach function to window for AppLLMApiService to access
if (typeof window !== "undefined") {
	window.calculatePrebuiltAppSets = calculatePrebuiltAppSets;
	console.log("calculatePrebuiltAppSets attached to window object");
}

export const useAppSearchInterface = (
	initialFilters = {},
	currentFilters = null,
) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [refinedQuery, setRefinedQuery] = useState("");
	const [isSearching, setIsSearching] = useState(false);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [searchSuggestions, setSearchSuggestions] = useState([]);

	const [searchResults, setSearchResults] = useState({
		apps: [],
		keywords: [],
		totalRecords: 0,
		searchSummary: null,
		hasMore: false,
		currentPage: 1,
		totalPages: 1,
	});

	// FIXED: Simplified prebuilt sets state management
	const [prebuiltAppSets, setPrebuiltAppSets] = useState({});
	const [error, setError] = useState(null);

	// Track the last search terms to detect changes
	const lastSearchTermsRef = useRef(null);

	// LLM results storage
	const llmResultsRef = useRef(null);

	// Performance metrics state
	const [performanceMetric, setPerformanceMetric] = useState("impressions");
	const [minReachThreshold, setMinReachThreshold] = useState("100");

	// Debounce timer for suggestions
	const suggestionTimer = useRef(null);

	// Memoize stable values to prevent unnecessary re-renders
	const stableInitialFilters = useMemo(
		() => initialFilters,
		[JSON.stringify(initialFilters)],
	);

	// FIXED: Simplified initialization
	useEffect(() => {
		console.log("AppSearchInterface: Initializing state...");

		// Check for globally stored LLM results
		if (typeof window !== "undefined") {
			const globalSets = window.currentAppPrebuiltSets || {};

			if (Object.keys(globalSets).length > 0) {
				console.log(
					"Found global prebuilt sets on init:",
					Object.keys(globalSets),
				);
				setPrebuiltAppSets(globalSets);
			}

			if (window.appLLMSearchResults) {
				llmResultsRef.current = window.appLLMSearchResults;
				console.log("Found stored LLM results on init");
			}
		}

		// Initialize other state
		setSearchResults({
			apps: [],
			keywords: [],
			totalRecords: 0,
			searchSummary: null,
			hasMore: false,
			currentPage: 1,
			totalPages: 1,
		});
		setError(null);
		setIsSearching(false);
		setShowSuggestions(false);
		setSearchSuggestions([]);

		// Cleanup function
		return () => {
			if (suggestionTimer.current) {
				clearTimeout(suggestionTimer.current);
			}
		};
	}, []);

	// CRITICAL FIX: Function to clear ALL prebuilt sets storage locations
	const clearAllPrebuiltSetsStorage = useCallback(() => {
		console.log("🧹 Clearing ALL prebuilt sets storage locations");

		// Clear React state
		setPrebuiltAppSets({});

		// Clear all global storage locations
		if (typeof window !== "undefined") {
			delete window.currentAppPrebuiltSets;
			delete window.lastAppLLMPrebuiltSets;

			if (window.globalAppLLMResults) {
				delete window.globalAppLLMResults.prebuiltSets;
				delete window.globalAppLLMResults.prebuiltAppSets;
			}
		}

		// Clear session storage backup
		sessionStorage.removeItem("prebuiltSetsBackup");

		console.log("✅ All prebuilt sets storage cleared");
	}, []);

	// CRITICAL FIX: Function to detect if search terms changed
	const hasSearchTermsChanged = useCallback((newSearchTerms) => {
		const currentTerms = Array.isArray(newSearchTerms) ? newSearchTerms : [];
		const lastTerms = lastSearchTermsRef.current || [];

		// Compare lengths
		if (currentTerms.length !== lastTerms.length) {
			return true;
		}

		// Compare actual terms
		const currentTexts = currentTerms
			.map((term) => (typeof term === "object" ? term.text : term))
			.filter(Boolean)
			.sort();

		const lastTexts = lastTerms
			.map((term) => (typeof term === "object" ? term.text : term))
			.filter(Boolean)
			.sort();

		return JSON.stringify(currentTexts) !== JSON.stringify(lastTexts);
	}, []);

	// FIXED: Enhanced setter that updates storage locations
	const setPrebuiltAppSetsWithPersistence = useCallback((newSets) => {
		console.log("📊 Updating prebuilt sets:", Object.keys(newSets));

		setPrebuiltAppSets(newSets);

		// Update global storage for other components
		if (typeof window !== "undefined") {
			window.currentAppPrebuiltSets = newSets;
			window.lastAppLLMPrebuiltSets = newSets;

			if (window.globalAppLLMResults) {
				window.globalAppLLMResults.prebuiltSets = newSets;
				window.globalAppLLMResults.prebuiltAppSets = newSets;
			}
		}
	}, []);

	// FIXED: Function to store LLM results
	const storeLLMResults = useCallback(
		(llmData, generatedSets = null) => {
			console.log("📱 Storing LLM results for prebuilt sets:", {
				hasData: !!llmData,
				hasSets: !!generatedSets,
				setsKeys: generatedSets ? Object.keys(generatedSets) : [],
			});

			llmResultsRef.current = {
				data: llmData,
				timestamp: new Date().toISOString(),
				prebuiltSets: generatedSets,
			};

			if (generatedSets && Object.keys(generatedSets).length > 0) {
				setPrebuiltAppSetsWithPersistence(generatedSets);
			}
		},
		[setPrebuiltAppSetsWithPersistence],
	);

	// Handle search input changes and fetch app keyword suggestions
	const handleSearchInputChange = useCallback(async (e) => {
		const value = e.target.value;
		setSearchQuery(value);

		// Clear previous timer
		if (suggestionTimer.current) {
			clearTimeout(suggestionTimer.current);
		}

		if (value.length >= 2) {
			setShowSuggestions(true);

			// Debounce suggestions fetch
			suggestionTimer.current = setTimeout(async () => {
				try {
					const result = await appApiService.getAppKeywords(value);

					if (result.success && Array.isArray(result.keywords)) {
						setSearchSuggestions(result.keywords.slice(0, 8));
					} else {
						setSearchSuggestions([]);
					}
				} catch (err) {
					setSearchSuggestions([]);
				}
			}, 300);
		} else {
			setShowSuggestions(false);
			setSearchSuggestions([]);
		}
	}, []);

	// CRITICAL FIX: Enhanced app search function with proper prebuilt sets clearing
	const handleAppSearch = useCallback(
		async (searchData, searchOptions = {}) => {
			try {
				setIsSearching(true);
				setError(null);

				// CRITICAL: Detect if this is a NEW search with different terms
				const isNewSearchWithTerms = hasSearchTermsChanged(searchData);

				console.log("🔍 App search analysis:", {
					isNewSearch: isNewSearchWithTerms,
					hasSearchData: !!searchData,
					searchDataLength: Array.isArray(searchData) ? searchData.length : 0,
					isFilterUpdate: searchOptions?.isFilterUpdate,
					searchTerms: Array.isArray(searchData)
						? searchData.map((t) => t.text || t)
						: [],
				});

				// CRITICAL FIX: Clear prebuilt sets for ANY new search with different terms
				if (
					isNewSearchWithTerms &&
					!searchOptions?.isFilterUpdate &&
					!searchOptions?.overrideResults
				) {
					console.log("🧹 NEW SEARCH DETECTED - Clearing all prebuilt sets");
					clearAllPrebuiltSetsStorage();
					llmResultsRef.current = null;
				}

				// Update the last search terms reference
				if (searchData) {
					lastSearchTermsRef.current = Array.isArray(searchData)
						? [...searchData]
						: [searchData];
				}

				// Transform search data to expected format
				let searchTerms = [];

				if (
					searchData &&
					typeof searchData === "object" &&
					!Array.isArray(searchData)
				) {
					if (searchData.query && Array.isArray(searchData.query)) {
						searchData.query.forEach((item) => {
							searchTerms.push({
								text: item.query,
								type: "include",
								threshold: item.threshold ?? DEFAULT_TERM_THRESHOLD,
								score: Math.round(
									relevanceFromDistance(
										item.threshold ?? DEFAULT_TERM_THRESHOLD,
										"APPS",
									),
								),
							});
						});
					}

					if (
						searchData.excludeQuery &&
						Array.isArray(searchData.excludeQuery)
					) {
						searchData.excludeQuery.forEach((item) => {
							searchTerms.push({
								text: item.query,
								type: "exclude",
								threshold: item.threshold ?? DEFAULT_TERM_THRESHOLD,
								score: Math.round(
									relevanceFromDistance(
										item.threshold ?? DEFAULT_TERM_THRESHOLD,
										"APPS",
									),
								),
							});
						});
					}
				} else if (Array.isArray(searchData)) {
					searchTerms = searchData;
				} else if (typeof searchData === "string") {
					searchTerms = [
					{
						text: searchData,
						type: "include",
						threshold: DEFAULT_TERM_THRESHOLD,
						score: Math.round(
							relevanceFromDistance(DEFAULT_TERM_THRESHOLD, "APPS"),
						),
					},
				];
				}

				// Check if we have override results (from LLM)
				if (searchOptions?.overrideResults) {
					console.log("📱 Using override results from LLM");
					const overrideResults = searchOptions.overrideResults;

					// Process the override results
					const processedResults = processSearchResponse(
						overrideResults,
						searchTerms,
						searchOptions?.filters || {},
					);

					setSearchResults({
						apps: processedResults.apps || [],
						keywords: processedResults.keywords || [],
						totalRecords: processedResults.totalRecords || 0,
						searchSummary: processedResults.searchSummary || null,
						hasMore: false,
						currentPage: 1,
						totalPages: 1,
						originalData: {
							raw: overrideResults,
							metadata: {
								searchTime: new Date().toISOString(),
								searchTerms,
								isLLMEnhanced: true,
							},
						},
						success: true,
						appliedFilters: searchOptions?.filters || {},
					});

					// Handle prebuilt sets from override results
					if (
						processedResults.prebuiltSets &&
						Object.keys(processedResults.prebuiltSets).length > 0
					) {
						setPrebuiltAppSetsWithPersistence(processedResults.prebuiltSets);
						console.log("✅ Set prebuilt app sets from override results");
					}

					return { success: true, data: processedResults };
				}

				// Check authentication
				if (!appApiService.isAuthenticated()) {
					console.log("⚠️ Not authenticated, triggering auth failure handler");
					if (apiService.handleAuthFailure) {
						apiService.handleAuthFailure(new Error("Authentication required"));
					}
					return { success: false, error: "Authentication required" };
				}

				// Extract options
				const {
					performanceMetric: optionsPerformanceMetric = performanceMetric,
					minValue: passedMinValue,
					performanceGoalType = "reach",
					sortBy: passedSortBy = "compositeScore",
					sortOrder: passedSortOrder = "desc",
					dateRange = 30,
					filters: passedFilters,
					startIndex = 0,
					noOfResult: passedNoOfResult,
					limit: passedLimit,
				} = searchOptions;

				const finalMinValue = passedMinValue || 100000000;
				const finalNoOfResult =
					passedNoOfResult ||
					passedLimit ||
					passedFilters?.noOfResult ||
					passedFilters?.limit ||
					1000;

				// Use currentFilters if provided
				let filtersToUse;
				if (
					passedFilters &&
					(passedFilters.minMarketRequests || passedFilters.sortBy)
				) {
					filtersToUse = passedFilters;
				} else {
					filtersToUse = currentFilters || stableInitialFilters;
				}

				// Prepare enhanced filters
				const enhancedFilters = {
					...filtersToUse,
					startIndex,
					noOfResult: finalNoOfResult,
					limit: finalNoOfResult,
					noOfDays: dateRange,
					minMarketRequests: finalMinValue,
					marketRequestsThreshold: finalMinValue,
					minValue: finalMinValue,
					sortBy: passedSortBy,
					sortOrder: passedSortOrder,
					view: passedSortOrder,
					performanceGoalType: performanceGoalType,
				};

				// Map performance goal type to API format
				let apiPerformanceMetric = optionsPerformanceMetric;
				if (performanceGoalType === "market_request") {
					apiPerformanceMetric = "reach";
				}

				const result = await appApiService.performAppSearch(
					searchTerms,
					enhancedFilters,
					apiPerformanceMetric,
					finalMinValue,
					{
						dateRange,
						startIndex,
						noOfResult: finalNoOfResult,
						sortBy: passedSortBy,
						sortOrder: passedSortOrder,
					},
				);

				if (result.success) {
					const responseData = result.data || {};
					const processedResults = processSearchResponse(
						responseData,
						searchTerms,
						enhancedFilters,
					);

					setSearchResults({
						apps: processedResults.apps || [],
						keywords: processedResults.keywords || [],
						totalRecords: processedResults.totalRecords || 0,
						searchSummary: processedResults.searchSummary || null,
						hasMore: processedResults.hasMore || false,
						currentPage: Math.floor(startIndex / finalNoOfResult) + 1,
						totalPages: Math.ceil(
							(processedResults.totalRecords || 0) / finalNoOfResult,
						),
						originalData: {
							raw: responseData,
							statusCode: result.statusCode,
							metadata: {
								searchTime: new Date().toISOString(),
								searchTerms,
								filters: enhancedFilters,
								performanceMetric: apiPerformanceMetric,
								minValue: finalMinValue,
								sortBy: passedSortBy,
								sortOrder: passedSortOrder,
								pagination: {
									startIndex,
									noOfResult: finalNoOfResult,
									currentPage: Math.floor(startIndex / finalNoOfResult) + 1,
								},
							},
						},
						success: true,
						appliedFilters: enhancedFilters,
					});

					// CRITICAL FIX: Handle prebuilt sets properly for new searches
					if (
						processedResults.prebuiltSets &&
						Object.keys(processedResults.prebuiltSets).length > 0
					) {
						console.log("📊 NEW prebuilt sets generated from search results");
						setPrebuiltAppSetsWithPersistence(processedResults.prebuiltSets);

						// Store backup ONLY for the current search
						sessionStorage.setItem(
							"prebuiltSetsBackup",
							JSON.stringify(processedResults.prebuiltSets),
						);
					} else {
						console.log(
							"⚠️ No new prebuilt sets in response - this should trigger clearing",
						);
						// If no new sets and this is a new search, clear everything
						if (isNewSearchWithTerms) {
							clearAllPrebuiltSetsStorage();
						}
					}

					console.log("✅ App search completed successfully:", {
						totalApps: processedResults.apps?.length || 0,
						totalRecords: processedResults.totalRecords || 0,
						hasMore: processedResults.hasMore,
						prebuiltSetsCount: Object.keys(processedResults.prebuiltSets || {})
							.length,
					});

					return {
						success: true,
						data: processedResults,
						metadata: {
							searchTerms,
							filters: enhancedFilters,
							performanceMetric: apiPerformanceMetric,
							sortBy: passedSortBy,
							sortOrder: passedSortOrder,
							resultLimit: finalNoOfResult,
							pagination: {
								currentPage: Math.floor(startIndex / finalNoOfResult) + 1,
								totalPages: Math.ceil(
									(processedResults.totalRecords || 0) / finalNoOfResult,
								),
								hasMore: processedResults.hasMore,
							},
						},
					};
				} else {
					throw new Error(
						result.error || "App search failed - no data returned",
					);
				}
			} catch (err) {
				const errorMessage = err.message || "App search failed";
				setError(errorMessage);

				console.error("❌ App search error:", {
					error: errorMessage,
					searchData,
					searchOptions,
				});

				return { success: false, error: errorMessage };
			} finally {
				setIsSearching(false);
			}
		},
		[
			performanceMetric,
			minReachThreshold,
			stableInitialFilters,
			currentFilters,
			hasSearchTermsChanged,
			clearAllPrebuiltSetsStorage,
			setPrebuiltAppSetsWithPersistence,
		],
	);

	// FIXED: Enhanced process search response
	const processSearchResponse = useCallback(
		(responseData, searchTerms, filters) => {
			console.log("🔄 Processing app search response:", responseData);

			let apps = [];
			let keywords = [];
			let totalRecords = 0;
			let hasMore = false;
			let prebuiltSets = {};

			const actualData = responseData.body || responseData;
			const isLLMEnhanced =
				actualData.isLLMEnhanced || responseData.isLLMEnhanced;

			if (actualData.data && Array.isArray(actualData.data)) {
				apps = actualData.data;
				totalRecords = actualData.totalCount || actualData.total || apps.length;
				keywords = actualData.keywords || [];

				// ENHANCED: Prebuilt sets detection with priority order
				console.log("🔍 Checking for prebuilt sets in response");

				// Priority 1: Check if there's fresh data from API
				if (
					actualData.cumulativeData &&
					Array.isArray(actualData.cumulativeData) &&
					actualData.cumulativeData.length > 0
				) {
					console.log(
						"🔧 Using calculator to process cumulative data for app sets",
					);
					prebuiltSets = calculatePrebuiltAppSets(responseData);

					if (Object.keys(prebuiltSets).length > 0) {
						console.log(
							"✅ Generated NEW prebuilt app sets from cumulative data:",
							Object.keys(prebuiltSets),
						);
					}
				}
				// Priority 2: Check direct response prebuilt sets
				else if (
					responseData.prebuiltSets &&
					Array.isArray(responseData.prebuiltSets)
				) {
					const foundPrebuiltSets = responseData.prebuiltSets;
					prebuiltSets = foundPrebuiltSets.reduce((acc, setName, index) => {
						acc[setName] = {
							id: setName,
							name: setName,
							apps: [],
							metadata: { source: "llm", index },
						};
						return acc;
					}, {});
					console.log(
						"✅ Found prebuilt sets from direct response:",
						Object.keys(prebuiltSets),
					);
				}
				// Priority 3: Check actualData prebuilt sets
				else if (
					actualData.prebuiltSets &&
					Array.isArray(actualData.prebuiltSets)
				) {
					const foundPrebuiltSets = actualData.prebuiltSets;
					prebuiltSets = foundPrebuiltSets.reduce((acc, setName, index) => {
						acc[setName] = {
							id: setName,
							name: setName,
							apps: [],
							metadata: { source: "llm", index },
						};
						return acc;
					}, {});
					console.log(
						"✅ Found prebuilt sets from actualData:",
						Object.keys(prebuiltSets),
					);
				} else {
					console.log("🔭 No new prebuilt sets in this response");
					// Don't preserve old sets - let them be cleared if this is a new search
					prebuiltSets = {};
				}
			} else {
				// Handle other response structures
				if (responseData.body?.data && Array.isArray(responseData.body.data)) {
					apps = responseData.body.data;
					totalRecords =
						responseData.body.totalCount ||
						responseData.body.total ||
						apps.length;
				} else if (responseData.data && Array.isArray(responseData.data)) {
					apps = responseData.data;
					totalRecords =
						responseData.totalCount || responseData.total || apps.length;
				} else if (
					responseData.results &&
					Array.isArray(responseData.results)
				) {
					apps = responseData.results;
					totalRecords =
						responseData.totalCount || responseData.total || apps.length;
				} else if (Array.isArray(responseData)) {
					apps = responseData;
					totalRecords = apps.length;
				} else {
					console.warn("⚠️ Unexpected response structure:", responseData);
					apps = [];
				}
			}

			// Extract keywords if available
			if (responseData.keywords && Array.isArray(responseData.keywords)) {
				keywords = responseData.keywords;
			}

			// Calculate hasMore for pagination
			const startIndex = filters.startIndex || 0;
			const noOfResult = filters.noOfResult || 100;
			hasMore = startIndex + apps.length < totalRecords;

			// Normalize app data structure (existing logic remains the same)
			const normalizedApps = apps.map((app, index) => {
				return {
					// Core identifiers
					id: app.id || app.bundle_id || `app-${index}`,
					bundle_id: app.bundle_id || app.bundleId || app.package_name,

					// App information
					app_name: app.app_name || app.name || app.title,
					name: app.app_name || app.name || app.title,
					developer: app.developer || app.creator || app.publisher,
					creator: app.developer || app.creator || app.publisher,
					publisher: app.publisher || app.developer || app.creator,

					// Categories and classification
					category: app.category || app.subcategory || app.genre,
					subcategory: app.subcategory || app.category,
					content_rating:
						app.content_rating || app.contentRating || app.rating_content,

					// Platform and market info
					platform: app.platform || "unknown",
					market_source: app.market_source || app.marketSource || app.source,
					market_status: app.market_status || app.status,

					// Ratings and reviews
					rating: parseFloat(app.rating || app.app_rating || 0) || 0,
					rating_count:
						parseInt(
							app.rating_count || app.ratingCount || app.reviews_count || 0,
						) || null,

					// Downloads and installs
					downloads:
						parseInt(
							app.downloads || app.installs || app.download_count || 0,
						) || null,

					// Dates
					release_date: app.release_date || app.releaseDate || app.created_date,
					update_date: app.update_date || app.updateDate || app.last_updated,

					// Performance metrics (advertising)
					totalImpressions:
						parseInt(app.totalImpressions || app.impressions || 0) || null,
					impressions:
						parseInt(app.totalImpressions || app.impressions || 0) || null,
					totalCprClicks:
						parseInt(app.totalCprClicks || app.clicks || 0) || null,
					clicks: parseInt(app.totalCprClicks || app.clicks || 0) || null,
					calculated_ctr:
						parseFloat(app.calculated_ctr || app.ctr || 0) || null,
					ctr: parseFloat(app.calculated_ctr || app.ctr || 0) || null,
					avgViewability:
						parseFloat(app.avgViewability || app.viewability || 0) || null,
					viewability:
						parseFloat(app.avgViewability || app.viewability || 0) || null,
					avgVtr: parseFloat(app.avgVtr || app.vtr || 0) || null,
					vtr: parseFloat(app.avgVtr || app.vtr || 0) || null,

					// Financial metrics
					calculated_costperimpression:
						parseFloat(app.calculated_costperimpression || app.cpm || 0) ||
						null,
					cpm: parseFloat(app.avgBidCpm || app.avgBidCpm || 0) || null,
					calculated_revenueperimpression:
						parseFloat(
							app.calculated_revenueperimpression / 1000 || app.rpi / 1000 || 0,
						) || null,
					rpi:
						parseFloat(
							app.calculated_revenueperimpression / 1000 || app.rpi / 1000 || 0,
						) || null,

					// Descriptions
					description: app.description || app.short_description || app.summary,
					short_description:
						app.short_description || app.description || app.summary,

					// Additional fields
					languages: app.languages || [],
					keywords: app.keywords || [],
					domain: app.domain || app.app_domain,

					// Store original data for debugging
					_original: app,
				};
			});

			// Create search summary
			const searchSummary = {
				totalApps: normalizedApps.length,
				totalRecords,
				searchTerms: searchTerms
					.map((term) => `${term.type === "include" ? "+" : "-"}${term.text}`)
					.join(" "),
				appliedFilters: Object.keys(filters).filter(
					(key) =>
						filters[key] &&
						(Array.isArray(filters[key]) ? filters[key].length > 0 : true),
				),
				sortBy: filters.sortBy,
				sortOrder: filters.sortOrder,
				timestamp: new Date().toISOString(),
			};

			console.log("✅ App search response processed:", {
				originalAppsCount: apps.length,
				normalizedAppsCount: normalizedApps.length,
				totalRecords,
				hasMore,
				keywordsCount: keywords.length,
				prebuiltSetsCount: Object.keys(prebuiltSets).length,
			});

			return {
				apps: normalizedApps,
				keywords,
				totalRecords,
				hasMore,
				searchSummary,
				prebuiltSets,
			};
		},
		[setPrebuiltAppSetsWithPersistence],
	);

	// Rest of the methods remain the same but simplified
	const handleSearchWithLogging = useCallback(
		async (searchData, searchOptions = {}) => {
			console.log("🔍 App search initiated with data:", searchData);
			return handleAppSearch(searchData, searchOptions);
		},
		[handleAppSearch],
	);

	const handlePageChange = useCallback(
		async (page) => {
			if (!searchResults.originalData?.metadata?.searchTerms) {
				console.warn("⚠️ No previous search data available for pagination");
				return;
			}

			const {
				searchTerms,
				filters,
				performanceMetric,
				minValue,
				sortBy,
				sortOrder,
				resultLimit,
			} = searchResults.originalData.metadata;
			const pageNoOfResult =
				resultLimit || filters.noOfResult || filters.limit || 1000;
			const startIndex = (page - 1) * pageNoOfResult;

			const paginationOptions = {
				...filters,
				startIndex,
				noOfResult: pageNoOfResult,
				limit: pageNoOfResult,
				performanceMetric,
				minValue,
				sortBy,
				sortOrder,
				isFilterUpdate: true, // Mark as filter update to preserve prebuilt sets
			};

			return handleSearchWithLogging(searchTerms, paginationOptions);
		},
		[searchResults.originalData?.metadata, handleSearchWithLogging],
	);

	const handleSuggestionSelect = useCallback((suggestion) => {
		try {
			let suggestionText = "";

			if (typeof suggestion === "string") {
				suggestionText = suggestion;
			} else if (suggestion?.keyword) {
				suggestionText = suggestion.keyword;
			} else if (suggestion?.text) {
				suggestionText = suggestion.text;
			} else if (suggestion?.name) {
				suggestionText = suggestion.name;
			}

			if (suggestionText.trim()) {
				setSearchQuery(suggestionText);
				setShowSuggestions(false);
			}
		} catch (err) {
			console.warn("⚠️ App suggestion selection error:", err);
		}
	}, []);

	const handleRefinedQueryChange = useCallback((value) => {
		setRefinedQuery(value);
	}, []);

	const handleClearRefinement = useCallback(() => {
		setRefinedQuery("");
		setSearchResults({
			apps: [],
			keywords: [],
			totalRecords: 0,
			searchSummary: null,
			hasMore: false,
			currentPage: 1,
			totalPages: 1,
		});
		setError(null);
	}, []);

	const handleHideSuggestions = useCallback(() => {
		setShowSuggestions(false);
	}, []);

	const handlePerformanceMetricChange = useCallback((metric) => {
		setPerformanceMetric(metric);
	}, []);

	const handleMinReachChange = useCallback((value) => {
		setMinReachThreshold(value);
	}, []);

	// FIXED: Reset function with proper clearing
	const resetSearch = useCallback(
		(clearPrebuiltSets = true) => {
			setSearchQuery("");
			setRefinedQuery("");
			setIsSearching(false);
			setShowSuggestions(false);
			setSearchSuggestions([]);
			setSearchResults({
				apps: [],
				keywords: [],
				totalRecords: 0,
				searchSummary: null,
				hasMore: false,
				currentPage: 1,
				totalPages: 1,
			});

			if (clearPrebuiltSets) {
				clearAllPrebuiltSetsStorage();
				llmResultsRef.current = null;
			}

			// Clear the last search terms reference
			lastSearchTermsRef.current = null;

			setError(null);

			if (suggestionTimer.current) {
				clearTimeout(suggestionTimer.current);
			}
		},
		[clearAllPrebuiltSetsStorage],
	);

	const getSearchSummary = useCallback(() => {
		if (!searchResults) return null;

		return {
			totalRecords: searchResults.totalRecords || 0,
			apps: searchResults.apps?.length || 0,
			keywords: searchResults.keywords?.length || 0,
			searchTime: searchResults.originalData?.metadata?.searchTime,
			appliedFilters: searchResults.appliedFilters,
			sortBy: searchResults.originalData?.metadata?.sortBy,
			sortOrder: searchResults.originalData?.metadata?.sortOrder,
			pagination: {
				currentPage: searchResults.currentPage || 1,
				totalPages: searchResults.totalPages || 1,
				hasMore: searchResults.hasMore || false,
			},
		};
	}, [searchResults]);

	// FIXED: Load prebuilt app sets function
	const loadPrebuiltAppSets = useCallback(async () => {
		try {
			console.log("📱 Loading prebuilt app sets...");

			// Check current state first
			if (Object.keys(prebuiltAppSets).length > 0) {
				console.log("📊 Using existing prebuilt sets from state");
				return {
					success: true,
					data: prebuiltAppSets,
					message: `Using ${Object.keys(prebuiltAppSets).length} existing prebuilt sets`,
				};
			}

			// Check global storage
			if (typeof window !== "undefined") {
				const globalSets = window.currentAppPrebuiltSets || {};

				if (Object.keys(globalSets).length > 0) {
					console.log(
						"✅ Found global prebuilt sets:",
						Object.keys(globalSets),
					);
					setPrebuiltAppSetsWithPersistence(globalSets);
					return {
						success: true,
						data: globalSets,
						message: `Loaded ${Object.keys(globalSets).length} prebuilt sets from global storage`,
					};
				}
			}

			// Check LLM results
			if (llmResultsRef.current?.prebuiltSets) {
				console.log("🎯 Found prebuilt sets from stored LLM results");
				const sets = llmResultsRef.current.prebuiltSets;
				setPrebuiltAppSetsWithPersistence(sets);
				return {
					success: true,
					data: sets,
					message: `Loaded ${Object.keys(sets).length} prebuilt sets from LLM results`,
				};
			}

			console.log("🔭 No prebuilt sets found");
			return {
				success: true,
				data: {},
				message:
					"No prebuilt sets available. Perform a search to generate prebuilt sets.",
			};
		} catch (error) {
			console.error("❌ Error loading prebuilt app sets:", error);
			return {
				success: false,
				error: error.message,
				message: "Failed to load prebuilt sets",
			};
		}
	}, [prebuiltAppSets, setPrebuiltAppSetsWithPersistence]);

	// Memoized computed values
	const computedValues = useMemo(
		() => ({
			hasResults:
				searchResults?.apps?.length > 0 || searchResults?.keywords?.length > 0,
			hasPrebuiltSets: Object.keys(prebuiltAppSets).length > 0,
			hasError: !!error,
			isLoadingMore: isSearching && searchResults?.apps?.length > 0,
			canLoadMore: searchResults?.hasMore && !isSearching,
		}),
		[searchResults, prebuiltAppSets, error, isSearching],
	);

	// Cleanup effect
	useEffect(() => {
		return () => {
			if (suggestionTimer.current) {
				clearTimeout(suggestionTimer.current);
			}
		};
	}, []);

	// FIXED: Force clear function
	const forceResetAppSearch = useCallback(
		(clearPrebuiltSets = true) => {
			console.log(
				"🔄 Force clearing app search state...",
				clearPrebuiltSets
					? "(including prebuilt sets)"
					: "(preserving prebuilt sets)",
			);

			setSearchQuery("");
			setRefinedQuery("");
			setIsSearching(false);
			setShowSuggestions(false);
			setSearchSuggestions([]);
			setSearchResults({
				apps: [],
				keywords: [],
				totalRecords: 0,
				searchSummary: null,
				hasMore: false,
				currentPage: 1,
				totalPages: 1,
			});

			if (clearPrebuiltSets) {
				clearAllPrebuiltSetsStorage();
				llmResultsRef.current = null;
			}

			// Clear search terms reference
			lastSearchTermsRef.current = null;

			setError(null);

			if (suggestionTimer.current) {
				clearTimeout(suggestionTimer.current);
			}

			console.log("✅ App search state cleared");
		},
		[clearAllPrebuiltSetsStorage],
	);

	const cleanup = useCallback(() => {
		if (suggestionTimer.current) {
			clearTimeout(suggestionTimer.current);
		}
	}, []);

	return {
		// Core search state
		searchQuery,
		refinedQuery,
		isSearching,
		showSuggestions,
		searchSuggestions,
		searchResults,
		error,

		// App-specific prebuilt sets - now properly cleared on new searches
		prebuiltAppSets,

		// Performance state
		performanceMetric,
		minReachThreshold,

		// Event handlers
		onSearchInputChange: handleSearchInputChange,
		onRefinedQueryChange: handleRefinedQueryChange,
		onSearch: handleSearchWithLogging,
		onSuggestionSelect: handleSuggestionSelect,
		onClearRefinement: handleClearRefinement,
		onHideSuggestions: handleHideSuggestions,
		onPerformanceMetricChange: handlePerformanceMetricChange,
		onMinReachChange: handleMinReachChange,
		onPageChange: handlePageChange,

		// Utility functions
		resetSearch,
		getSearchSummary,
		loadPrebuiltAppSets,
		cleanup,
		forceResetAppSearch,
		storeLLMResults,

		// Computed values
		...computedValues,
	};
};
